import { CharacterCount as Count } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit as Base } from '@tiptap/starter-kit';
import { HTMLAttributes, useEffect, useRef, type JSX } from 'react';

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
} from '../../../../primitives/input-group';
import { LoadingAddon } from '../../../../primitives/loading-addon';
import { cn } from '../../../../utils/cn';
import { ExtensionsProps } from '../../extensions';
import { Mention } from '../../extensions/mention';
import { parseContentFromSchema } from '../../utils/parse-content-from-schema';
import { CharacterCount } from '../character-count';

import 'prosemirror-view/style/prosemirror.css';
import '../../styles.css';
import styles from './styles.module.css';

const EXTENSIONS = [
  Base.configure({
    heading: false,
    bold: false,
    italic: false,
    blockquote: false,
    code: false,
    codeBlock: false,
    strike: false,
    underline: false,
    orderedList: false,
    bulletList: false,
    listItem: false,
    horizontalRule: false,
    dropcursor: false,
    gapcursor: false,
  }),
  Count,
];

interface EditorPlainProps extends Pick<HTMLAttributes<HTMLDivElement>, 'className'> {
  button?: {
    disabled?: boolean;
    icon?: JSX.Element;
    id?: string;
    onClick: VoidFunction;
    text?: string;
  };
  counter?: { enable: boolean; hasWords?: boolean };
  disabled?: boolean;
  ext?: ExtensionsProps;
  id?: string;
  isLoading?: boolean;
  type?: 'textarea' | 'input';
  value: string;
  onChange: (value: string) => void;
}

export function EditorPlain({
  button,
  className,
  counter,
  disabled = false,
  ext,
  id,
  isLoading = false,
  type = 'textarea',
  value,
  onChange,
}: EditorPlainProps) {
  const isSyncingContentRef = useRef(true);
  const editor = useEditor({
    editorProps: {
      attributes: {
        class: cn(
          styles['editor-content-plain'],
          'w-full rounded-none border-0 shadow-none focus-within:ring-0 focus-visible:ring-0',
          {
            'min-h-28': type === 'textarea',
            'min-h-9': type === 'input',
            'opacity-50': disabled,
          },
          className,
        ),
        'data-slot': 'input-group-control',
        id: id ?? '',
      },
    },
    extensions: [
      ...EXTENSIONS,
      ...(ext?.mention?.items?.length
        ? [
            Mention.configure({
              items: ext?.mention?.items,
            }),
          ]
        : []),
    ],
    injectCSS: true,
    onUpdate: ({ editor }) => {
      if (isSyncingContentRef.current) return;
      onChange(editor.getText());
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.getText() === value) {
      isSyncingContentRef.current = false;
      return;
    }

    isSyncingContentRef.current = true;
    const frameId = requestAnimationFrame(() => {
      if (!editor.isDestroyed) {
        editor.commands.setContent(parseContentFromSchema({ content: value, editor }), {
          emitUpdate: false,
        });
      }
      isSyncingContentRef.current = false;
    });
    return () => cancelAnimationFrame(frameId);
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) return null;

  const hasCounter = Boolean(counter?.enable);
  const { icon, id: buttonId, onClick: buttonClick, text } = button ?? {};
  const hasButton = typeof buttonClick === 'function';

  return (
    <InputGroup
      className={cn('h-auto flex-col items-stretch', {
        'cursor-not-allowed': disabled || isLoading,
      })}
      disabled={disabled || isLoading}
    >
      <div className="w-full">
        <EditorContent editor={editor} />
      </div>
      {hasCounter || hasButton ? (
        <InputGroupAddon align="block-end" className="flex-wrap justify-end gap-4">
          {hasCounter ? (
            <InputGroupText>
              <CharacterCount className="p-0" editor={editor} hasWords={counter?.hasWords} />
            </InputGroupText>
          ) : null}
          {hasButton ? (
            <InputGroupButton
              className="flex-none"
              disabled={disabled || button?.disabled || isLoading}
              id={buttonId}
              size="xs"
              title={text ?? 'Acción'}
              variant="secondary"
              onClick={buttonClick}
            >
              {icon}
              {text}
            </InputGroupButton>
          ) : null}
        </InputGroupAddon>
      ) : null}
      {isLoading && <LoadingAddon />}
    </InputGroup>
  );
}
